import { forwardRef, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";

import { Button, type ButtonProps } from "@/components/ui/button";
import { Input, type InputProps } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BRAND } from "@/brand/brand";
import { EASE_OUT, SPRING_LAYOUT, SPRING_SWAP } from "@/lib/ease";
import { cn } from "@/lib/utils";
import { shadcnTokensFor } from "@/themeTokens";
import { useCustomerTheme } from "../../Theme";
import "./authTheme.css";

/**
 * Shared building blocks of the sign-in, sign-up and password-reset screens:
 * a narrow, left-aligned column on the bare page (no card), small logo, title
 * with a one-line subtitle, labelled fields and a full-width primary action.
 *
 * Motion rules: a step swap slides and fades the whole step, the column
 * follows the new content height with a spring, and anything revealed inside
 * a step (password after the email check, error lines) grows out of zero
 * height. All of it collapses to instant changes under reduced motion.
 */

const STEP_TRANSITION = { duration: 0.32, ease: EASE_OUT };

/** Duration of the fade-and-shrink before a redirect, in ms. */
export const LEAVE_MS = 500;
const LEAVE_TRANSITION = { duration: LEAVE_MS / 1000, ease: EASE_OUT };

/** Page frame with the brand logo above the column. */
export const AuthShell = ({
  logoSrc,
  logoAlt,
  showLogo = true,
  width = 400,
  headerAction,
  leaving = false,
  logoHidden = false,
  children,
}: {
  logoSrc: string;
  logoAlt: string;
  /** False while a tenant theme is loading, to avoid a default-logo flash. */
  showLogo?: boolean;
  /** Column width in px; wide sign-up steps (company, licenses) widen it. */
  width?: number;
  /**
   * Link on the right of the logo row (e.g. "← Back to sign in"). It lives up
   * here so a step that has one starts its title at the same height as one
   * that has none.
   */
  headerAction?: ReactNode;
  /** Fades and shrinks the whole column away before a redirect (as in the app). */
  leaving?: boolean;
  /** Fades the logo out in place (its row keeps its height, nothing moves up). */
  logoHidden?: boolean;

  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  // A brand that floods the page with its own color (FireIQ) keeps a card
  // surface under the column, so the default foreground stays readable.
  const flooded = !!BRAND.loginBackgroundColor;
  const { palette, morphing } = useLoginPalette();
  const ready = useAssetsReady(showLogo ? logoSrc : undefined, showLogo);

  return (
    <div
      className={cn(
        "flex min-h-screen w-full items-start justify-center bg-background px-4 pb-10 pt-10 sm:pt-[max(2.5rem,16vh)]",
        morphing && "auth-theme-morph",
      )}
      style={{
        ...palette,
        ...(flooded ? { backgroundColor: BRAND.loginBackgroundColor! } : {}),
      }}
    >
      <motion.div
        className={cn(
          "flex w-full flex-col",
          // Held back (layout already in place) until logo and fonts are
          // there, then everything appears in one frame — nothing pops in late.
          !ready && "invisible",
          flooded && "rounded-xl bg-background p-6 shadow-lg sm:p-8",
        )}
        initial={false}
        animate={{
          maxWidth: width + (flooded ? 64 : 0),
          opacity: leaving ? 0 : 1,
          scale: leaving ? 0.95 : 1,
        }}
        transition={
          reduce
            ? { duration: 0 }
            : { ...SPRING_LAYOUT, opacity: LEAVE_TRANSITION, scale: LEAVE_TRANSITION }
        }
      >
        <div className="mb-8 flex h-9 items-center justify-between gap-4">
          {showLogo && (
            <button
              type="button"
              aria-label={t("header.home")}
              onClick={() => navigate("/")}
              className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <motion.span
                className="block"
                initial={false}
                animate={{ opacity: logoHidden ? 0 : 1, filter: logoHidden ? "blur(4px)" : "blur(0px)" }}
                transition={reduce ? { duration: 0 } : { duration: 0.35, ease: EASE_OUT }}
              >
                <LogoSwap src={logoSrc} alt={logoAlt} />
              </motion.span>
            </button>
          )}
          <AnimatePresence mode="wait">
            {headerAction && (
              <motion.div
                key="header-action"
                initial={reduce ? false : { opacity: 0, x: 8, filter: "blur(4px)" }}
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                exit={reduce ? undefined : { opacity: 0, x: 8, filter: "blur(4px)" }}
                transition={{ duration: 0.35, ease: EASE_OUT }}
              >
                {headerAction}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {children}
      </motion.div>
    </div>
  );
};

/**
 * True once the logo image is decoded and the web fonts are loaded — or after
 * READY_TIMEOUT_MS at the latest, so a slow image never keeps the form hidden.
 * Also waits while `gate` is false (a tenant theme still loading).
 */
const READY_TIMEOUT_MS = 1500;

// Logos already shown once: a remount (login ↔ sign-up) is ready at once
// instead of hiding the column for a frame.
const readyLogos = new Set<string>();

const useAssetsReady = (logoSrc: string | undefined, gate: boolean) => {
  const [ready, setReady] = useState(() => gate && !!logoSrc && readyLogos.has(logoSrc));
  useEffect(() => {
    if (ready) {
      if (logoSrc) readyLogos.add(logoSrc);
      return;
    }
    const timeout = setTimeout(() => setReady(true), READY_TIMEOUT_MS);
    if (!gate) return () => clearTimeout(timeout);
    let cancelled = false;
    const image = logoSrc ? new Image() : undefined;
    const logo = image
      ? new Promise<void>((resolve) => {
          image.onload = () => resolve();
          image.onerror = () => resolve();
          image.src = logoSrc!;
        })
      : Promise.resolve();
    const fonts = typeof document !== "undefined" && document.fonts ? document.fonts.ready : Promise.resolve();
    Promise.all([logo, fonts]).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [logoSrc, gate, ready]);
  return ready;
};

/**
 * The sign-in palette: the brand's `loginTheme` as scoped CSS variables while
 * no tenant theme is known, nothing once one is (the body variables of the
 * tenant theme then show through). `morphing` is true for the moment the
 * palette changes, so colors glide over instead of switching.
 */
const MORPH_MS = 900;

const useLoginPalette = (): { palette: CSSProperties | undefined; morphing: boolean } => {
  const { theme } = useCustomerTheme();
  // A tenant theme comes from the server and names its customer; the
  // built-in default theme does not.
  const tenantThemed = !!theme?.customer;
  const palette =
    !tenantThemed && BRAND.loginTheme
      ? (shadcnTokensFor(BRAND.loginTheme.primary) as CSSProperties)
      : undefined;
  const key = tenantThemed ? `${theme.customer}:${theme.primary}` : "login";

  // `settledKey` trails `key` by MORPH_MS: while they differ the morph class
  // is on, set in the same commit as the new colors so the transition catches them.
  const [settledKey, setSettledKey] = useState(key);
  useEffect(() => {
    if (key === settledKey) return;
    const timer = setTimeout(() => setSettledKey(key), MORPH_MS);
    return () => clearTimeout(timer);
  }, [key, settledKey]);

  return { palette, morphing: key !== settledKey };
};

/**
 * Brand or tenant logo. A new source is loaded first and then cross-fades
 * over the old one (blur and a slight scale), so the switch to the tenant's
 * logo mid-login never flashes an empty or half-loaded image.
 */
const LogoSwap = ({ src, alt }: { src: string; alt: string }) => {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(src);

  useEffect(() => {
    if (src === shown) return;
    let cancelled = false;
    const image = new Image();
    const show = () => {
      if (!cancelled) setShown(src);
    };
    image.onload = show;
    image.onerror = show;
    image.src = src;
    return () => {
      cancelled = true;
    };
  }, [src, shown]);

  return (
    <span className="relative grid h-9 items-center">
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.img
          key={shown}
          src={shown}
          alt={alt}
          className="col-start-1 row-start-1 h-9 w-auto max-w-[180px] object-contain object-left"
          initial={reduce ? false : { opacity: 0, scale: 0.94, filter: "blur(6px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          exit={reduce ? undefined : { opacity: 0, scale: 1.04, filter: "blur(6px)" }}
          transition={{ duration: 0.6, ease: EASE_OUT }}
          style={{ transformOrigin: "left center" }}
        />
      </AnimatePresence>
    </span>
  );
};

/** Grows and shrinks with its content instead of jumping. */
export const AutoHeight = ({ children, className }: { children: ReactNode; className?: string }) => {
  const reduce = useReducedMotion();
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | "auto">("auto");

  useLayoutEffect(() => {
    const node = innerRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      setHeight(entry.borderBoxSize?.[0]?.blockSize ?? node.offsetHeight);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      className={cn("-mx-1 overflow-hidden px-1", className)}
      initial={false}
      animate={{ height }}
      transition={reduce ? { duration: 0 } : SPRING_LAYOUT}
    >
      <div ref={innerRef}>{children}</div>
    </motion.div>
  );
};

/**
 * One screen of a flow. Swapping `stepKey` slides the old step out and the new
 * one in; `direction` picks the side (forward = from the right).
 */
export const AuthStep = ({
  stepKey,
  direction = 1,
  enterOnMount = false,
  children,
}: {
  stepKey: string;
  direction?: 1 | -1;
  /** Slide the first step in too — used when arriving from the sibling page (login ↔ sign-up). */
  enterOnMount?: boolean;
  children: ReactNode;
}) => {
  const reduce = useReducedMotion();
  const offset = reduce ? 0 : 24 * direction;
  return (
    <AutoHeight>
      <AnimatePresence mode="wait" initial={enterOnMount}>
        <motion.div
          key={stepKey}
          initial={{ opacity: 0, x: offset, filter: reduce ? "none" : "blur(4px)" }}
          animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, x: -offset, filter: reduce ? "none" : "blur(4px)" }}
          transition={reduce ? { duration: 0 } : STEP_TRANSITION}
          className="flex flex-col pb-1"
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </AutoHeight>
  );
};

/** Title plus a one-line muted subtitle (which may carry a link). */
export const AuthHeader = ({
  title,
  subtitle,
  subtitleHidden = false,
  hidden = false,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Folds the subtitle away. */
  subtitleHidden?: boolean;
  /** Folds the whole header away (e.g. once the code is verified). */
  hidden?: boolean;
}) => (
  <Reveal show={!hidden}>
    <div className="flex flex-col pb-6">
      <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
      {subtitle && (
        <Reveal show={!subtitleHidden}>
          <p className="pt-1.5 text-sm text-muted-foreground">{subtitle}</p>
        </Reveal>
      )}
    </div>
  </Reveal>
);

/** Reveals its children by growing out of zero height. */
export const Reveal = ({
  show,
  children,
  className,
}: {
  show: boolean;
  children: ReactNode;
  className?: string;
}) => {
  const reduce = useReducedMotion();
  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={reduce ? { duration: 0 } : { ...SPRING_LAYOUT, opacity: { duration: 0.2 } }}
          className={cn("-mx-1 overflow-hidden px-1", className)}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/** Error line under a field or form; slides open and closed. */
export const AuthError = ({ id, message }: { id?: string; message?: ReactNode }) => (
  <Reveal show={!!message}>
    <p id={id} role="alert" className="break-words pt-1.5 text-sm text-destructive">
      {message}
    </p>
  </Reveal>
);

/** Label above an input with its error line underneath. */
export const AuthField = forwardRef<
  HTMLInputElement,
  InputProps & {
    id: string;
    label: ReactNode;
    error?: ReactNode;
    /** Small link on the right of the label, e.g. "Change" next to a locked email. */
    labelAction?: ReactNode;
  }
>(({ id, label, error, labelAction, className, ...inputProps }, ref) => {
  const errorId = `${id}-error`;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {labelAction}
      </div>
      <Input
        ref={ref}
        id={id}
        // No focus ring here: a slightly darker border marks the active field
        // instead, so focus stays visible without a colored halo.
        className={cn(
          "h-10 shadow-none transition-colors focus-visible:border-foreground/40 focus-visible:ring-0 aria-invalid:focus-visible:border-destructive",
          className,
        )}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        {...inputProps}
      />
      <AuthError id={errorId} message={error} />
    </div>
  );
});
AuthField.displayName = "AuthField";

/**
 * `busy` as the user should see it: a round-trip that ends within
 * SPINNER_DELAY_MS never shows a spinner (no flicker on a fast answer), and
 * one that does show stays at least SPINNER_MIN_MS.
 */
const SPINNER_DELAY_MS = 300;
const SPINNER_MIN_MS = 400;

const useCalmBusy = (busy: boolean) => {
  const [shown, setShown] = useState(false);
  const shownAt = useRef(0);
  useEffect(() => {
    if (busy && !shown) {
      const timer = setTimeout(() => {
        shownAt.current = Date.now();
        setShown(true);
      }, SPINNER_DELAY_MS);
      return () => clearTimeout(timer);
    }
    if (!busy && shown) {
      const wait = Math.max(0, SPINNER_MIN_MS - (Date.now() - shownAt.current));
      const timer = setTimeout(() => setShown(false), wait);
      return () => clearTimeout(timer);
    }
  }, [busy, shown]);
  return shown;
};

/** Full-width primary action whose label cross-fades when it changes. */
export const AuthSubmit = ({
  busy = false,
  children,
  className,
  ...props
}: ButtonProps & { busy?: boolean }) => {
  const reduce = useReducedMotion();
  const spinning = useCalmBusy(busy);
  const label = typeof children === "string" ? children : undefined;
  return (
    <Button
      type="submit"
      className={cn("relative h-10 w-full overflow-hidden", className)}
      {...props}
      // Disabled while busy without the faded look, so a quick answer leaves
      // the button visually untouched.
      disabled={props.disabled && !busy}
      aria-disabled={busy || props.disabled || undefined}
      onClick={busy ? (event) => event.preventDefault() : props.onClick}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={spinning ? "busy" : label ?? "label"}
          className="inline-flex items-center"
          initial={reduce ? false : { opacity: 0, y: 10, filter: "blur(2px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={reduce ? undefined : { opacity: 0, y: -10, filter: "blur(2px)" }}
          transition={{ duration: 0.25, ease: EASE_OUT }}
        >
          {spinning ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : children}
        </motion.span>
      </AnimatePresence>
      {spinning && <span className="sr-only">{label}</span>}
    </Button>
  );
};

/** Horizontal rule with a centered caption ("OR"). */
export const OrDivider = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3 py-1" role="separator" aria-label={label}>
    <span className="h-px flex-1 bg-border" />
    <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
    <span className="h-px flex-1 bg-border" />
  </div>
);

/** Muted line with a bold inline action, e.g. "No account? Sign up". */
export const AuthFootnote = ({
  text,
  action,
  onAction,
  className,
}: {
  text: ReactNode;
  action: ReactNode;
  onAction: () => void;
  className?: string;
}) => (
  <p className={cn("text-sm text-muted-foreground", className)}>
    {text}{" "}
    <button
      type="button"
      onClick={onAction}
      className="rounded-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {action}
    </button>
  </p>
);

/** Four-square Microsoft mark for the Entra sign-in button. */
export const MicrosoftMark = () => (
  <svg aria-hidden="true" viewBox="0 0 21 21" className="h-4 w-4">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

/** Two-option switch with a sliding highlight (SMS vs. authenticator app). */
export const SegmentedChoice = <T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  label: string;
}) => {
  const reduce = useReducedMotion();
  const layoutId = `segment-${useId()}`;
  return (
    <div role="radiogroup" aria-label={label} className="grid grid-flow-col auto-cols-fr rounded-lg bg-muted p-1">
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              "relative rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              selected ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {selected && (
              <motion.span
                layoutId={layoutId}
                className="absolute inset-0 rounded-md bg-background shadow-sm"
                transition={reduce ? { duration: 0 } : SPRING_LAYOUT}
              />
            )}
            <span className="relative">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

/** Small "← Back" link for the logo row of `AuthShell`. */
export const AuthBackLink = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="group inline-flex items-center gap-1.5 rounded-sm text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  >
    <ArrowLeft aria-hidden="true" className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
    {label}
  </button>
);

/**
 * "Step n of m" with one segment per step for the logo row. On mount the
 * segments rise in one after another and the done ones fill from the left;
 * on a step change the number rolls and the next segment fills.
 */
export const StepCounter = ({
  current,
  total,
  label,
}: {
  /** 1-based. */
  current: number;
  total: number;
  label: string;
}) => {
  const reduce = useReducedMotion();
  return (
    <div className="flex items-center gap-2.5" role="status" aria-label={label}>
      <span aria-hidden="true" className="relative overflow-hidden text-xs tabular-nums text-muted-foreground">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={label}
            className="inline-block"
            initial={reduce ? false : { y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={reduce ? undefined : { y: -10, opacity: 0 }}
            transition={SPRING_SWAP}
          >
            {label}
          </motion.span>
        </AnimatePresence>
      </span>
      <div className="flex gap-1" aria-hidden="true">
        {Array.from({ length: total }, (_, index) => (
          <motion.span
            key={index}
            className="relative h-1 w-4 overflow-hidden rounded-full bg-muted"
            initial={reduce ? false : { opacity: 0, scaleX: 0.3 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.4, ease: EASE_OUT, delay: reduce ? 0 : 0.12 + index * 0.05 }}
          >
            <motion.span
              className="absolute inset-0 origin-left rounded-full bg-primary"
              initial={reduce ? false : { scaleX: 0 }}
              animate={{ scaleX: index < current ? 1 : 0 }}
              transition={
                reduce
                  ? { duration: 0 }
                  : { ...SPRING_LAYOUT, delay: 0.3 + Math.max(0, index) * 0.05 }
              }
            />
          </motion.span>
        ))}
      </div>
    </div>
  );
};

/**
 * Takes the place of the code field once the code is accepted, as in the
 * app: the ring draws itself, a check is drawn into it and a single pulse
 * rings outward. No text — `label` is for screen readers only.
 */
export const SuccessCheck = ({ label }: { label: string }) => {
  const reduce = useReducedMotion();
  const instant = { duration: 0 };
  return (
    <div className="flex flex-col items-center py-6" role="status" aria-live="polite" aria-label={label}>
      <div className="relative h-14 w-14 text-primary">
        {!reduce && (
          <motion.span
            aria-hidden="true"
            className="absolute inset-0 rounded-full border-2 border-current"
            initial={{ scale: 1, opacity: 0 }}
            animate={{ scale: [1, 1.6], opacity: [0.35, 0] }}
            transition={{ duration: 0.8, ease: EASE_OUT, delay: 0.5 }}
          />
        )}
        <motion.span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-primary"
          initial={reduce ? false : { scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={reduce ? instant : { type: "spring", stiffness: 380, damping: 18 }}
        />
        <svg aria-hidden="true" viewBox="0 0 56 56" className="relative h-14 w-14 -rotate-90">
          <motion.circle
            cx="28"
            cy="28"
            r="26"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            initial={reduce ? false : { pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={reduce ? instant : { duration: 0.5, ease: EASE_OUT }}
          />
        </svg>
        <svg aria-hidden="true" viewBox="0 0 56 56" className="absolute inset-0 h-14 w-14 text-primary-foreground">
          <motion.path
            d="M18 29 L25 36 L38 21"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={reduce ? false : { pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={reduce ? instant : { duration: 0.32, ease: EASE_OUT, delay: 0.35 }}
          />
        </svg>
      </div>
    </div>
  );
};
